import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel } from '@tanstack/react-table';

import { colors, css, fonts, frames, getGraphColor, glyphs, Units } from '../../../Styles';
import { ButtonList, PageHeader } from '../../../Components';
import { ButtonTypes, ListData_ValueTypes, TableStyles_Types } from '../../../Definitions'
import Graph_QL, { useFeedComponents } from '../../../Services/Agrisys_GqlDataCaller';
import { useAppSelector } from '../../../Store';
import { ReactiveTable } from '../../../Components/Tables/reactiveTableRenderer';

import { keyBy, merge } from 'lodash';
import { GenericDataParameters } from '../../../Components/Tables/_Tables/useColumnConstructor';
import { FeedComponentInfo } from '../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedComponentsCall';
import { useEditableColumnConstructor } from '../../../Components/Tables/_Tables/useEditableColumnConstructor';

const getComponentTableDivStyle = (id?: string | number):CSSProperties|undefined => { if (!id) {return undefined} return {backgroundColor: getGraphColor(+id), ...css.colorTag, ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary}}
interface DirtyData {
  id: number,
  [key: string]: string|number|boolean|undefined
}

const feedComponentAccessorKeys = {
  name: ListData_ValueTypes.Div,
  readyMixture: ListData_ValueTypes.Check,
  priority: ListData_ValueTypes.Number,
  ds: ListData_ValueTypes.Number,
  energy: ListData_ValueTypes.Number,
  protein: ListData_ValueTypes.Number,
  delete: ListData_ValueTypes.DeleteRowAction,
}

const getUnitFromAccessor = (accessor: keyof FeedComponentInfo) => {
  if (accessor === "ds" || accessor === "protein") {
    return Units.pct_white
  }
  if (accessor === "energy") {
    return Units.kG_white
  }
  return 
}

export default function Components() {
  const {t} = useTranslation();

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);

  const [editTable, setEditTable] = useState(false);
  const [addedRowCounter, setAddedRowCount] = useState(1);

  const {call: getFeedComponentsInfo, data: feedComponentsState, maintainEntries} = useFeedComponents(connection);
  // Switch between edit mode and non-edit mode
  const switchTable = () => {
    setEditTable((editTable) => !editTable);
  };

  const feedComponents = useMemo(() => feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data: undefined,[domain, feedComponentsState])
  const [data, setData] = useState(feedComponents?.filter((component) => !component.deleted) ?? []);

  useEffect(() => {
    if (feedComponents) {
      setData(feedComponents.filter((component) => !component.deleted));
    }
  },[feedComponents])
  const dirtRef = useRef<DirtyData[]>([]);

  const saveChangedData = () => {
    if (maintainEntries) {
      console.log(dirtRef.current)
      const data = dirtRef.current.map((dirtyData) => {
        const suggestedDataEntry = {...feedComponents?.find((component) => component.id === dirtyData.id), ...dirtyData };
        delete suggestedDataEntry["__typename"];
        return suggestedDataEntry;
      });
      maintainEntries({con: connection.connection, data: data});
      dirtRef.current = [];
    }
  }

  const clearDirt = useCallback(() => {
    if (feedComponents) {
      setData(feedComponents.filter((component) => !component.deleted));
      dirtRef.current = [];
    }
  },[feedComponents])

  const addRow = useCallback(() => {
    setEditTable(true);
    setAddedRowCount(addedRowCounter + 1);
    setData([{id: -addedRowCounter, name: "", readyMixture: false, priority: 0, ds: 0, energy: 0, protein: 0, deleted: false}, ...Object.values(merge([], keyBy(data, "id"), keyBy(dirtRef.current, "id"))).filter((row) => !row.deleted)]);
  },[addedRowCounter, data])

  useEffect(() => {
    getFeedComponentsInfo(connection, {fetchAmount: 50});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const accessorData = useMemo<GenericDataParameters[]>(() => {
    return Object.entries(feedComponentAccessorKeys).map(([accessor, type]) => {
      return {
        headerValue: t(`feed.feedPlan.${accessor}`, {defaultValue: type}),
        accessor,
        type,
        unit: getUnitFromAccessor(accessor as keyof FeedComponentInfo),
        width: accessor==="delete" ? "56px" : undefined,
        sort: accessor ==="delete" ? false : true,
        divStyleOverride: type===ListData_ValueTypes.Div ? getComponentTableDivStyle : undefined,
        styleOverrideOnValue: false,
      }
    })
  },[t]);
  const columns = useEditableColumnConstructor<FeedComponentInfo>({accessorData, tableStyle: TableStyles_Types.newTertiaryTable, canEdit: editTable})
  
  const table = useReactTable({
    columns: columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta: {
      updateData: (id, columnId, value) => {
        dirtRef.current = (Object.values(merge(keyBy(dirtRef.current, "id"), keyBy([{id, [columnId]: value}], "id"))))
      },
      deleteRow: (deletedRowId) => {
        dirtRef.current = Object.values(merge(keyBy(dirtRef.current, "id"),keyBy([{id: deletedRowId, deleted: true}], "id")));
        setData(Object.values(merge([], keyBy(data, "id"), keyBy(dirtRef.current, "id"))).filter((row) => !row.deleted));
      }
    }
  })

  if (!feedComponents) { return (<div></div>)}
  return (
    <frames.Page_inner>
      <frames._page_inner_header>
        <PageHeader topHeader={t("globalMenu.feedCurves")} subHeader={t("feed.feedPlan.components")}/>
      </frames._page_inner_header>
      <frames._page_inner_content>
        <div style={{height: "100%", width: "100%", display: "grid", gridAutoRows: "50px 1fr", boxSizing: "border-box"}}>
          <div style={{ backgroundColor: "transparent", display: "flex", justifyContent: "end", padding: "0px 20px", gap: "5px"}}>
            {!editTable? 
              <ButtonList 
                contents={[t("buttons.add"), t("buttons.edit")]} 
                onClick={(i => {
                  if (i === 0) addRow()
                  else if (i === 1) switchTable()
                })}
                buttonType={ButtonTypes.tableButton}
                startEnhancers={[glyphs.Add({}), glyphs.Edit({})]}
              />
            : <ButtonList 
                contents={[t("buttons.saveBtn"), t("buttons.cancelBtn"), t("buttons.add"), t("buttons.edit")]} 
                onClick={(i => {
                  if (i === 0) { saveChangedData(); switchTable() }
                  else if (i === 1) { clearDirt(); switchTable() }
                })}
                buttonType={ButtonTypes.tableButton}
                startEnhancers={[glyphs.Check({}), glyphs.Close({}), glyphs.Add({}), glyphs.Edit({})]}
                disabled={[false, false, true, true]}
                overrides={(index) => ({
                  baseButton: {
                    backgroundColor: index === 3 ? colors.greyPrimary : colors.greySecondary
                  },
                  disabledStyles: {
                    opacity: index === 3 ? 1 : 0.5
                  }
                })}
              />
            }
          </div>
          <div style={{height: "fit-content", maxHeight: "100%", boxSizing: "border-box", position: "relative", backgroundColor: colors.greyLightSecondary, border: "5px solid"+colors.greyPrimary, borderRadius: "10px", overflow: "hidden"}}>
            {<ReactiveTable table={table} tableType={TableStyles_Types.newTertiaryTable} emptyText={t("feedComponents.table.emptyText", {defaultValue: "No components found"})}/>}
          </div>
        </div>
      </frames._page_inner_content>
    </frames.Page_inner>
  );
}